import {
  VpnDebugAnyResult,
  VpnDebugCustomerResult,
  VpnDebugResult,
  isCustomerResult,
} from "../vpnDebugInterfaces";
import { STEP_ORDER } from "../vpnDebugConstants";
import { VpnDebugClassificationCode } from "../vpnDebugInterfaces";

/** The codes the banner knows how to style. */
const CLASSIFICATION_CODES: VpnDebugClassificationCode[] = [
  "tunnel_down",
  "routing_firewall",
  "service_unreachable",
  "auth_failure",
  "stream_encoding",
  "healthy",
  "inconclusive",
];

/**
 * Render both audiences through one set of components.
 *
 * The customer payload is a strict subset of the operator one, so it is
 * widened here rather than branching every renderer. The alternative -- an
 * `if (customer)` in each of StepCard, ClassificationBanner and the panel --
 * would put the tenancy boundary in three places and guarantee that the next
 * field added leaks through whichever one was missed.
 *
 * Fields the customer payload deliberately lacks are filled with neutral
 * values, never invented ones:
 *   - `evidence` is `{}`, not omitted, because the card reads it unguarded.
 *   - `decisive` is null. The server does not send it to customers, and
 *     `false` is a claim ("this step cannot move the verdict"), not a neutral
 *     value: it put a "Not decisive" pill on every customer step, a failed
 *     "Connection" included.
 *   - `confidence` is "", so nothing renders a confidence it was not told.
 *   - `owner` is "unknown". The server sends `owner_label` ("who acts next")
 *     precisely so a customer console never states fault; mapping it back to
 *     a real owner here would undo that on the client.
 */
export const widenCustomerResult = (
  result: VpnDebugCustomerResult
): VpnDebugResult => ({
  schema_version: result.schema_version,
  tunnel: {
    id: result.tunnel?.id ?? 0,
    name: "",
    gateway_name: "",
    gateway_instance_id: "",
  },
  target: { host: "" },
  planned_step_ids: [...STEP_ORDER],
  steps: (result.steps || []).map((step) => ({
    id: step.id,
    order: step.order,
    title: step.title,
    status: step.status,
    decisive: null,
    reason_code: step.reason_code,
    summary: step.summary,
    diagnosis: step.detail || null,
    next_action: step.next_action,
    skipped_reason: step.status === "skipped" ? step.detail || null : null,
    partial: false,
    confidence: "",
    evidence: {},
    duration_ms: null,
  })),
  classification: {
    // Same code vocabulary on both sides, so the banner colours the verdict
    // correctly; anything unrecognised falls back rather than throwing.
    code: (CLASSIFICATION_CODES.includes(
      result.verdict?.code as VpnDebugClassificationCode
    )
      ? result.verdict.code
      : "inconclusive") as VpnDebugClassificationCode,
    label: result.verdict?.owner_label || "",
    owner: "unknown",
    confidence: "",
    summary: result.verdict?.headline || "",
    evidence_step_ids: [],
    next_actions: result.verdict?.next_actions || [],
  },
  degraded: [],
  cli_missing: false,
});

/** Pass the operator payload through; widen the customer one. */
export const normalizeVpnDebugResult = (
  result: VpnDebugAnyResult | null
): VpnDebugResult | null => {
  if (!result) return null;
  return isCustomerResult(result) ? widenCustomerResult(result) : result;
};
